import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Loader2, Save, Bell, Mail, Key, Clock, AlertTriangle, RefreshCw, Eye, EyeOff } from 'lucide-react';

const SettingsPage = () => {
  const { api, user, isAdmin, changePassword } = useAuth();
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('account');

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Log filter state
  const [logFilterType, setLogFilterType] = useState('all');
  const [logFilterStatus, setLogFilterStatus] = useState('all');
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchLogs = useCallback(async (type = logFilterType, status = logFilterStatus) => {
    if (!isAdmin) return;
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (type && type !== 'all') params.set('type', type);
      if (status && status !== 'all') params.set('status', status);
      const response = await api.get(`/settings/notification-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load notification logs');
    } finally {
      setLogsLoading(false);
    }
  }, [api, isAdmin, logFilterType, logFilterStatus]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        api.get('/settings/notifications'),
        api.get('/users')
      ];

      if (isAdmin) {
        requests.push(api.get('/settings/notification-logs?limit=50'));
      }

      const responses = await Promise.all(requests);
      setConfig({
        weekly_update_reminder_day: 5,
        weekly_update_reminder_time: '15:00',
        ...responses[0].data,
      });
      setUsers(responses[1].data);

      if (isAdmin && responses[2]) {
        setLogs(responses[2].data);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/notifications', config);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    const result = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    setChangingPassword(false);
    if (result.success) {
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  const toggleEnabledEmail = (email) => {
    setConfig(prev => ({
      ...prev,
      enabled_emails: prev.enabled_emails.includes(email)
        ? prev.enabled_emails.filter(e => e !== email)
        : [...prev.enabled_emails, email]
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge variant="success">SENT</Badge>;
      case 'failed':
        return <Badge variant="destructive">FAILED</Badge>;
      case 'pending':
        return <Badge variant="warning">PENDING</Badge>;
      case 'skipped':
        return <Badge variant="secondary">SKIPPED</Badge>;
      case 'permanently_failed':
        return <Badge variant="destructive">PERMANENTLY FAILED</Badge>;
      default:
        return <Badge variant="outline">{status.toUpperCase()}</Badge>;
    }
  };

  const handleLogFilterTypeChange = (value) => {
    setLogFilterType(value);
    fetchLogs(value, logFilterStatus);
  };

  const handleLogFilterStatusChange = (value) => {
    setLogFilterStatus(value);
    fetchLogs(logFilterType, value);
  };

  const handleRefreshLogs = () => {
    fetchLogs(logFilterType, logFilterStatus);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl tracking-tight uppercase">SETTINGS</h1>
        <p className="text-muted-foreground text-sm">
          Configure your preferences
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4">
          <TabsList className="w-max min-w-full sm:w-auto">
            <TabsTrigger value="account" data-testid="account-tab">
              <Key className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">ACCOUNT</span>
              <span className="sm:hidden text-xs">ACCOUNT</span>
            </TabsTrigger>
            {isAdmin && (
              <>
                <TabsTrigger value="notifications" data-testid="notifications-tab">
                  <Bell className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">NOTIFICATIONS</span>
                  <span className="sm:hidden text-xs">NOTIFS</span>
                </TabsTrigger>
                <TabsTrigger value="logs" data-testid="logs-tab">
                  <Mail className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">EMAIL LOGS</span>
                  <span className="sm:hidden text-xs">LOGS</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </div>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg uppercase flex items-center gap-2">
                <Key className="h-5 w-5" />
                CHANGE PASSWORD
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">CURRENT PASSWORD</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      required
                      className="pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">NEW PASSWORD</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      required
                      minLength={8}
                      className="pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">CONFIRM NEW PASSWORD</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      className="pr-10"
                      data-testid="confirm-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {passwordForm.newPassword && passwordForm.confirmPassword &&
                   passwordForm.newPassword !== passwordForm.confirmPassword && (
                    <p className="text-xs text-destructive">Passwords don't match</p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={changingPassword}
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  CHANGE PASSWORD
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg uppercase">ACCOUNT INFO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Name</p>
                <p className="text-sm">{user?.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Email</p>
                <p className="text-sm font-mono">{user?.email}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Role</p>
                <Badge variant={user?.role === 'admin' ? 'default' : 'secondary'}>
                  {user?.role?.toUpperCase()}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="notifications" className="space-y-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg uppercase flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  NOTIFICATION TYPES
                </CardTitle>
                <CardDescription>Enable or disable each notification type</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border border-border">
                    <div className="min-w-0">
                      <Label className="text-sm">Invitation Email</Label>
                      <p className="text-xs text-muted-foreground">Send email when user is invited</p>
                    </div>
                    <Switch
                      checked={config?.invitation_email}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, invitation_email: checked }))}
                      data-testid="invitation-email-switch"
                      className="shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border border-border">
                    <div className="min-w-0">
                      <Label className="text-sm">OTP Email</Label>
                      <p className="text-xs text-muted-foreground">Send OTP for password reset</p>
                    </div>
                    <Switch
                      checked={config?.otp_email}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, otp_email: checked }))}
                      data-testid="otp-email-switch"
                      className="shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border border-border">
                    <div className="min-w-0">
                      <Label className="text-sm">Task Assigned</Label>
                      <p className="text-xs text-muted-foreground">Notify when task is assigned</p>
                    </div>
                    <Switch
                      checked={config?.task_assigned}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, task_assigned: checked }))}
                      data-testid="task-assigned-switch"
                      className="shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border border-border">
                    <div className="min-w-0">
                      <Label className="text-sm">Deadline Warning</Label>
                      <p className="text-xs text-muted-foreground">Warn before task deadline</p>
                    </div>
                    <Switch
                      checked={config?.deadline_warning}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, deadline_warning: checked }))}
                      data-testid="deadline-warning-switch"
                      className="shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border border-border">
                    <div className="min-w-0">
                      <Label className="text-sm">Timesheet Reminder</Label>
                      <p className="text-xs text-muted-foreground">Daily reminder to log hours</p>
                    </div>
                    <Switch
                      checked={config?.timesheet_reminder}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, timesheet_reminder: checked }))}
                      data-testid="timesheet-reminder-switch"
                      className="shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border border-border">
                    <div className="min-w-0">
                      <Label className="text-sm">Weekly Summary</Label>
                      <p className="text-xs text-muted-foreground">Send weekly progress report</p>
                    </div>
                    <Switch
                      checked={config?.weekly_summary}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, weekly_summary: checked }))}
                      data-testid="weekly-summary-switch"
                      className="shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border border-border">
                    <div className="min-w-0">
                      <Label className="text-sm">Missed Timesheet Alert</Label>
                      <p className="text-xs text-muted-foreground">Alert admins about missed timesheets</p>
                    </div>
                    <Switch
                      checked={config?.missed_timesheet_alert}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, missed_timesheet_alert: checked }))}
                      data-testid="missed-timesheet-switch"
                      className="shrink-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border border-border">
                    <div className="min-w-0">
                      <Label className="text-sm">Weekly Update Reminder</Label>
                      <p className="text-xs text-muted-foreground">Remind users to submit weekly task updates</p>
                    </div>
                    <Switch
                      checked={config?.weekly_update_reminder}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, weekly_update_reminder: checked }))}
                      data-testid="weekly-update-reminder-switch"
                      className="shrink-0"
                    />
                  </div>
                  {config?.weekly_update_reminder && (
                    <div className="col-span-1 md:col-span-2 space-y-3 p-3 sm:p-4 border border-border" data-testid="reminder-schedule-fields">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <Label className="text-xs shrink-0">REMINDER DAY</Label>
                        <select
                          value={config.weekly_update_reminder_day ?? 5}
                          onChange={(e) => setConfig(prev => ({...prev, weekly_update_reminder_day: parseInt(e.target.value)}))}
                          className="flex h-9 w-full sm:w-auto rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                          data-testid="reminder-day-select"
                        >
                          <option value={0}>Sunday</option>
                          <option value={1}>Monday</option>
                          <option value={2}>Tuesday</option>
                          <option value={3}>Wednesday</option>
                          <option value={4}>Thursday</option>
                          <option value={5}>Friday</option>
                          <option value={6}>Saturday</option>
                        </select>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <Label className="text-xs shrink-0">REMINDER TIME</Label>
                        <Input
                          type="time"
                          value={config.weekly_update_reminder_time || '15:00'}
                          onChange={(e) => setConfig(prev => ({...prev, weekly_update_reminder_time: e.target.value}))}
                          className="w-full sm:w-40"
                          data-testid="reminder-time-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg uppercase flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  TIMING SETTINGS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="deadlineWarningDays">DEADLINE WARNING (DAYS BEFORE)</Label>
                    <Input
                      id="deadlineWarningDays"
                      type="number"
                      min="1"
                      max="14"
                      value={config?.deadline_warning_days || 2}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        deadline_warning_days: parseInt(e.target.value)
                      }))}
                      data-testid="deadline-warning-days-input"
                    />
                    <p className="text-xs text-muted-foreground">Days before deadline to send warning</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg uppercase flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  EMAIL RECIPIENTS
                </CardTitle>
                <CardDescription>Configure who receives notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>RECIPIENTS MODE</Label>
                  <Select
                    value={config?.recipients_mode || 'all'}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, recipients_mode: value }))}
                  >
                    <SelectTrigger data-testid="recipients-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Team Members</SelectItem>
                      <SelectItem value="admins">Admins Only</SelectItem>
                      <SelectItem value="assigned">Assigned Users Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3 pt-4 border-t border-border">
                  <Label>SPECIFIC RECIPIENTS (FOR WEEKLY SUMMARY)</Label>
                  <p className="text-xs text-muted-foreground">Select specific users to receive weekly summaries</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border border-input bg-background">
                    {users.map(u => (
                      <div key={u.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`email-${u.id}`}
                          checked={config?.enabled_emails?.includes(u.email)}
                          onCheckedChange={() => toggleEnabledEmail(u.email)}
                          data-testid={`email-toggle-${u.id}`}
                        />
                        <label
                          htmlFor={`email-${u.id}`}
                          className="text-sm cursor-pointer"
                        >
                          {u.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleSaveConfig}
                  disabled={saving}
                  className="mt-4"
                  data-testid="save-config-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  SAVE SETTINGS
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Logs Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="logs" className="space-y-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg uppercase flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  NOTIFICATION LOGS
                </CardTitle>
                <CardDescription>Recent email notification history</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filter Controls */}
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 mb-6">
                  <Select
                    value={logFilterType}
                    onValueChange={handleLogFilterTypeChange}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]" data-testid="log-filter-type">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="invitation">Invitation</SelectItem>
                      <SelectItem value="otp">OTP</SelectItem>
                      <SelectItem value="task_assigned">Task Assigned</SelectItem>
                      <SelectItem value="deadline_warning">Deadline Warning</SelectItem>
                      <SelectItem value="timesheet_reminder">Timesheet Reminder</SelectItem>
                      <SelectItem value="weekly_summary">Weekly Summary</SelectItem>
                      <SelectItem value="missed_timesheet">Missed Timesheet</SelectItem>
                      <SelectItem value="weekly_update_reminder">Weekly Update Reminder</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={logFilterStatus}
                    onValueChange={handleLogFilterStatusChange}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]" data-testid="log-filter-status">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="skipped">Skipped</SelectItem>
                      <SelectItem value="permanently_failed">Permanently Failed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleRefreshLogs}
                    disabled={logsLoading}
                    data-testid="refresh-logs-btn"
                  >
                    <RefreshCw className={`h-4 w-4 ${logsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                {/* Log Table */}
                {logsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : logs.length > 0 ? (
                  <div className="overflow-x-auto -mx-4 md:mx-0">
                    <table className="w-full text-sm min-w-[600px]">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="table-header">DATE</th>
                          <th className="table-header">TYPE</th>
                          <th className="table-header">RECIPIENT</th>
                          <th className="table-header">SUBJECT</th>
                          <th className="table-header">STATUS</th>
                          <th className="table-header">RETRIES</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map(log => (
                          <tr key={log.id} className="table-row" data-testid={`log-${log.id}`}>
                            <td className="py-3 px-4 text-xs font-mono">
                              {format(new Date(log.created_at), 'MMM d, HH:mm')}
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="outline" className="text-[10px]">
                                {log.type.toUpperCase()}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-xs font-mono">{log.recipient}</td>
                            <td className="py-3 px-4 text-xs max-w-[200px] truncate" title={log.subject}>
                              {log.subject}
                            </td>
                            <td className="py-3 px-4">{getStatusBadge(log.status)}</td>
                            <td className="py-3 px-4 text-center">{log.retry_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Mail className="h-10 w-10 text-muted-foreground/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">
                      No logs found
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {logFilterType !== 'all' || logFilterStatus !== 'all'
                        ? 'Try adjusting your filters to see more results.'
                        : 'No notification logs have been recorded yet.'}
                    </p>
                    {(logFilterType !== 'all' || logFilterStatus !== 'all') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-3"
                        onClick={() => {
                          setLogFilterType('all');
                          setLogFilterStatus('all');
                          fetchLogs('all', 'all');
                        }}
                        data-testid="clear-log-filters-btn"
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
